'use client';

/**
 * GlassLoader Component
 *
 * A branded loading spinner inspired by the consumer portal splash screen.
 * Features dual concentric rings spinning in opposite directions, a pulsing
 * center dot with glow, radial background glow, and animated message text.
 *
 * Supports fullPage, section, and inline variants with optional
 * pulsing logo, neon accent glow, and accessible reduced-motion fallback.
 */

import Box from '@mui/material/Box';
import { keyframes, type SxProps, styled, type Theme, useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import React from 'react';
import type { NeonAccent } from '../../theme/palette';
import { baseColors, getNeonStyles } from '../../theme/palette';
import { transientOptions } from '../../utils/styledUtils';

// ============================================================================
// Types
// ============================================================================

export interface GlassLoaderProps {
  /** Layout variant controlling size, positioning, and backdrop */
  variant?: 'fullPage' | 'section' | 'inline';
  /** Neon accent color for the spinner rings and glow */
  neonAccent?: NeonAccent;
  /** Text displayed below the spinner */
  message?: string;
  /**
   * Image source for a branded logo that pulses inside the spinner ring (replaces center dot).
   * Pass a single string for a theme-neutral asset, or `{ light, dark }` to swap the asset
   * based on the active theme — required when the logo asset includes a fill background
   * that won't blend with both light and dark backgrounds.
   */
  logo?: string | { light: string; dark: string };
  /** Alt text for the logo image */
  logoAlt?: string;
  /** Logo image size in pixels (defaults to ~40% of spinner size) */
  logoSize?: number;
  /** Spinner ring diameter in pixels (overrides variant default) */
  size?: number;
  /** Additional MUI sx overrides */
  sx?: SxProps<Theme>;
}

// ============================================================================
// Default sizes per variant
// ============================================================================

const DEFAULT_SIZES: Record<NonNullable<GlassLoaderProps['variant']>, number> = {
  fullPage: 56,
  section: 48,
  inline: 24,
};

// Default accent color when no neonAccent is provided (baseColors.primary[500])
const DEFAULT_COLOR = baseColors.primary[500];

// ============================================================================
// Utilities
// ============================================================================

/** Convert hex color to rgba string */
function hexToRgba(hex: string, alpha: number): string {
  const r = Number.parseInt(hex.slice(1, 3), 16);
  const g = Number.parseInt(hex.slice(3, 5), 16);
  const b = Number.parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

// ============================================================================
// Keyframe animations
// ============================================================================

const spinnerRotation = keyframes`
  from { transform: rotate(0deg); }
  to   { transform: rotate(360deg); }
`;

const dotPulse = keyframes`
  0%, 100% { transform: scale(1); opacity: 1; }
  50%      { transform: scale(1.3); opacity: 0.7; }
`;

const glowBreathe = keyframes`
  0%, 100% { transform: scale(1); opacity: 0.6; }
  50%      { transform: scale(1.15); opacity: 1; }
`;

const textFade = keyframes`
  0%, 100% { opacity: 0.5; }
  50%      { opacity: 0.8; }
`;

const logoPulse = keyframes`
  0%, 100% { transform: translate(-50%, -50%) scale(1); }
  50%      { transform: translate(-50%, -50%) scale(0.92); }
`;

// ============================================================================
// Styled primitives
// ============================================================================

/** Outer container: handles positioning per variant */
const Root = styled(
  Box,
  transientOptions,
)<{ $variant: NonNullable<GlassLoaderProps['variant']>; $isDark: boolean }>(({ $variant, $isDark }) => {
  const base: React.CSSProperties & Record<string, unknown> = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 24,
  };

  if ($variant === 'fullPage') {
    return {
      ...base,
      position: 'fixed',
      inset: 0,
      zIndex: 9999,
      background: $isDark ? baseColors.neutral[950] : baseColors.neutral[50],
      // When rendered before ThemeProvider hydrates, $isDark defaults to false
      // (MUI default = light). Use CSS media query so the background matches
      // the user's system preference without causing SSR hydration mismatch.
      ...(!$isDark && {
        '@media (prefers-color-scheme: dark)': {
          background: baseColors.neutral[950],
        },
      }),
    };
  }

  if ($variant === 'section') {
    return {
      ...base,
      width: '100%',
      height: '100%',
      minHeight: 120,
    };
  }

  // inline
  return {
    ...base,
    flexDirection: 'row',
    gap: 8,
  };
});

/** Radial glow behind the spinner (fullPage and section only) */
const RadialGlow = styled(
  'div',
  transientOptions,
)<{ $color: string; $isDark: boolean }>(({ $color, $isDark }) => ({
  position: 'absolute',
  width: 300,
  height: 300,
  borderRadius: '50%',
  background: `radial-gradient(circle, ${hexToRgba($color, $isDark ? 0.12 : 0.08)} 0%, transparent 70%)`,
  animation: `${glowBreathe} 3s ease-in-out infinite`,
  pointerEvents: 'none',
  '@media (prefers-reduced-motion: reduce)': {
    animation: 'none',
  },
}));

/** Spinner wrapper — positions the rings and center element */
const SpinnerContainer = styled(
  'div',
  transientOptions,
)<{ $size: number }>(({ $size }) => ({
  position: 'relative',
  width: $size,
  height: $size,
}));

/** Outer spinning ring — border-top arc */
const OuterRing = styled(
  'div',
  transientOptions,
)<{ $color: string; $isDark: boolean }>(({ $color, $isDark }) => ({
  position: 'absolute',
  inset: 0,
  border: `2.5px solid ${hexToRgba($color, $isDark ? 0.15 : 0.12)}`,
  borderTopColor: $color,
  borderRadius: '50%',
  animation: `${spinnerRotation} 1s cubic-bezier(0.4, 0, 0.2, 1) infinite`,
  '@media (prefers-reduced-motion: reduce)': {
    animationDuration: '4s',
  },
}));

/** Inner spinning ring — smaller, reverse direction, slower */
const InnerRing = styled(
  'div',
  transientOptions,
)<{ $color: string; $isDark: boolean }>(({ $color, $isDark }) => ({
  position: 'absolute',
  inset: 6,
  border: `2px solid ${hexToRgba($color, $isDark ? 0.15 : 0.12)}`,
  borderTopColor: $color,
  borderRadius: '50%',
  animation: `${spinnerRotation} 1.5s cubic-bezier(0.4, 0, 0.2, 1) infinite reverse`,
  opacity: 0.6,
  '@media (prefers-reduced-motion: reduce)': {
    animationDuration: '6s',
  },
}));

/** Pulsing center dot with box-shadow glow */
const CenterDot = styled(
  'div',
  transientOptions,
)<{ $color: string }>(({ $color }) => ({
  position: 'absolute',
  top: '50%',
  left: '50%',
  width: 8,
  height: 8,
  margin: '-4px 0 0 -4px',
  background: $color,
  borderRadius: '50%',
  boxShadow: `0 0 16px ${hexToRgba($color, 0.5)}, 0 0 48px ${hexToRgba($color, 0.2)}`,
  animation: `${dotPulse} 2s ease-in-out infinite`,
  '@media (prefers-reduced-motion: reduce)': {
    animation: 'none',
  },
}));

/** Pulsing logo image positioned at center (replaces dot when provided) */
const LogoImage = styled(
  'img',
  transientOptions,
)<{ $size: number }>(({ $size }) => ({
  position: 'absolute',
  top: '50%',
  left: '50%',
  width: $size,
  height: $size,
  objectFit: 'contain',
  animation: `${logoPulse} 2s ease-in-out infinite`,
  '@media (prefers-reduced-motion: reduce)': {
    animation: 'none',
  },
}));

/** Message text with fade animation */
const MessageText = styled(Typography)(() => ({
  animation: `${textFade} 2s ease-in-out infinite`,
  '@media (prefers-reduced-motion: reduce)': {
    animation: 'none',
  },
}));

// ============================================================================
// Component
// ============================================================================

export const GlassLoader: React.FC<GlassLoaderProps> = ({
  variant = 'section',
  neonAccent,
  message,
  logo,
  logoAlt = '',
  logoSize,
  size,
  sx,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  // Resolve spinner color
  const accentColor = neonAccent ? getNeonStyles(neonAccent, isDark).base : DEFAULT_COLOR;

  // Resolve spinner size
  const spinnerSize = size ?? DEFAULT_SIZES[variant];

  // Resolve logo size (default to ~40% of spinner for proportional fit inside inner ring)
  const resolvedLogoSize = logoSize ?? Math.round(spinnerSize * 0.4);

  // Resolve logo source for the active theme
  const resolvedLogoSrc = typeof logo === 'string' ? logo : logo ? (isDark ? logo.dark : logo.light) : undefined;

  const showMessage = variant !== 'inline' && message;
  const showGlow = variant !== 'inline';
  const showDualRings = variant !== 'inline';

  return (
    <Root $variant={variant} $isDark={isDark} sx={sx}>
      {showGlow && <RadialGlow $color={accentColor} $isDark={isDark} />}

      <SpinnerContainer $size={spinnerSize}>
        <OuterRing $color={accentColor} $isDark={isDark} />
        {showDualRings && <InnerRing $color={accentColor} $isDark={isDark} />}
        {resolvedLogoSrc ? (
          <LogoImage src={resolvedLogoSrc} alt={logoAlt} $size={resolvedLogoSize} />
        ) : (
          showDualRings && <CenterDot $color={accentColor} />
        )}
      </SpinnerContainer>

      {showMessage && (
        <MessageText
          variant="body2"
          sx={{
            fontWeight: 500,
            fontSize: 13,
            letterSpacing: 0.5,
            color: hexToRgba(accentColor, 0.5),
          }}
        >
          {message}
        </MessageText>
      )}
    </Root>
  );
};
